import { useState, useEffect, useCallback } from 'react';
import { userService } from '../../../services/userService';

interface MonthlyUserData {
    month: string;
    displayMonth: string;
    count: number;
    cumulativeTotal: number;
}

export const UserGrowthAnalytics = () => {
    const [monthlyData, setMonthlyData] = useState<MonthlyUserData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formatMonthDisplay = (monthKey: string): string => {
        const [year, month] = monthKey.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    const fetchUserGrowthData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const monthlyResults = await userService.getUsersCreatedInLast12Months();

            // Build monthly data with cumulative totals
            let cumulative = 0;
            const result: MonthlyUserData[] = monthlyResults.map((item) => {
                cumulative += item.count;
                return {
                    month: item.month,
                    displayMonth: formatMonthDisplay(item.month),
                    count: item.count,
                    cumulativeTotal: cumulative,
                };
            });

            setMonthlyData(result);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
            console.error('Failed to fetch user growth data:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUserGrowthData();
    }, [fetchUserGrowthData]);

    const maxCumulative = Math.max(...monthlyData.map((d) => d.cumulativeTotal), 1);
    const totalUsers = monthlyData.reduce((sum, d) => sum + d.count, 0);
    const averageMonthly = monthlyData.length > 0
        ? Math.round(totalUsers / monthlyData.length)
        : 0;

    return (
        <div className="space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="text-red-800 font-medium mb-1">Error</h3>
                    <p className="text-red-600">{error}</p>
                </div>
            )}

            {isLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-gray-600">Loading user growth data...</span>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-1">Last 12 Months Total</h3>
                            <p className="text-2xl font-bold text-gray-900">{totalUsers.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-1">Avg. Monthly Signups</h3>
                            <p className="text-2xl font-bold text-blue-600">{averageMonthly.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-1">Peak Month</h3>
                            <p className="text-2xl font-bold text-green-600">
                                {monthlyData.length > 0
                                    ? Math.max(...monthlyData.map((d) => d.count)).toLocaleString()
                                    : 0}
                            </p>
                        </div>
                    </div>

                    {/* Bar Chart */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cumulative User Growth</h3>
                        <div className="space-y-3">
                            {monthlyData.map((data) => {
                                const percentage = (data.cumulativeTotal / maxCumulative) * 100;
                                return (
                                    <div key={data.month} className="flex items-center gap-4">
                                        <div className="w-28 text-sm text-gray-600 truncate" title={data.displayMonth}>
                                            {data.displayMonth.split(' ')[0].substring(0, 3)} {data.displayMonth.split(' ')[1]}
                                        </div>
                                        <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                                            <div
                                                className="bg-blue-500 h-6 rounded-full transition-all duration-300"
                                                style={{ width: `${Math.max(percentage, 2)}%` }}
                                            ></div>
                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700">
                                                {data.cumulativeTotal.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Breakdown</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Month
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            New Users
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cumulative Total
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Growth
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {[...monthlyData].reverse().map((data, index, arr) => {
                                        const prevMonth = arr[index + 1];
                                        const growth = prevMonth && prevMonth.count > 0
                                            ? Math.round(((data.count - prevMonth.count) / prevMonth.count) * 100)
                                            : null;

                                        return (
                                            <tr key={data.month} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {data.displayMonth}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {data.count.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {data.cumulativeTotal.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {growth !== null ? (
                                                        <span className={growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                            {growth >= 0 ? '+' : ''}{growth}%
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
